/**
 * The closed set of machine-readable error codes carried in `ApiFailure.error.code`.
 *
 * The web client switches on these; the human-readable `message` is free to change
 * without breaking a caller. Keys are SCREAMING_SNAKE per CLAUDE.md §3.
 */
export const ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  BAD_REQUEST: 'BAD_REQUEST',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  /**
   * ⚠️ [P5a] THE ONE 403 IN THIS SYSTEM THAT SAYS WHY, AND THE EXCEPTION IS NARROW.
   *
   * CLAUDE.md §10 bans debug codes in a 403 body because "why" describes the PERMISSION
   * MODEL to an attacker. This code describes the caller's OWN TOKEN — that it is valid,
   * unexpired and correctly signed, but minted for the other surface — which they already
   * hold and already know. It discloses nothing about any role, any scope, any record or
   * any other person.
   *
   * What it buys is the difference between "your session ended, log in again" and "you are
   * at the wrong door". Collapsed into a 401, a native app refreshes a perfectly good token
   * for ever and never tells its user to sign in on the surface they actually have.
   *
   * ⚠️ IT IS EMITTED ONLY WHEN THE TOKEN FULLY VERIFIES AGAINST THE OTHER AUDIENCE.
   * Expired, forged, malformed and wrong-type all stay an opaque 401 — see
   * `services/token.service.ts`, whose header records that deliberate collapse and this
   * one exception to it.
   */
  WRONG_SURFACE: 'WRONG_SURFACE',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  DUPLICATE_KEY: 'DUPLICATE_KEY',
  RATE_LIMITED: 'RATE_LIMITED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  UNPROCESSABLE: 'UNPROCESSABLE',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export const ERROR_CODE_VALUES = Object.values(ERROR_CODES);
